/**
 * Domain detection utility for routing between behandelaar, client portal, and admin portal apps.
 *
 * Production domains:
 * - app.scrivio.nl → behandelaar app (default)
 * - mijn.scrivio.nl → client portal
 * - admin.scrivio.nl → admin portal
 *
 * Development/E2E (any non-production hostname):
 * - ?mode=admin → admin portal
 * - ?mode=client → client portal
 * - (default) → behandelaar app
 */

export type AppMode = 'behandelaar' | 'client' | 'admin'

export function getAppMode(): AppMode {
  const hostname = window.location.hostname

  // Production domains take precedence
  if (hostname.startsWith('admin.')) return 'admin'
  if (hostname.startsWith('mijn.')) return 'client'
  if (hostname.endsWith('.scrivio.nl')) return 'behandelaar'

  // Development/E2E: check ?mode= parameter for any non-production hostname
  // This includes localhost, 127.0.0.1, and Docker hostnames like 'frontend'
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('mode') === 'admin') return 'admin'
  if (urlParams.get('mode') === 'client') return 'client'
  return 'behandelaar'
}

/**
 * Login route for the current domain. The admin portal has its own login page;
 * every other domain uses `/login`.
 *
 * Deliberately reads the hostname rather than `getAppMode()`: `?mode=admin` is a
 * dev/E2E convenience on the SPA, not a separate deployment, so a token failure
 * there must not send the user to a route the dev server does not serve.
 */
export function getLoginPath(): string {
  return window.location.hostname.startsWith('admin.') ? '/admin/login' : '/login'
}

const APP_HOST_PREFIX = 'app.'
const ADMIN_HOST_PREFIX = 'admin.'

/**
 * The admin portal of the environment this page is served from.
 *
 * Derived from the current origin rather than written out: the three apps are
 * one image behind sibling hostnames, so `app.staging.scrivio.nl` belongs with
 * `admin.staging.scrivio.nl` and a literal `admin.scrivio.nl` would carry a
 * staging session into production (#4679). Off a deployed hostname the admin
 * portal is the same origin under `?mode=admin`, as {@link getAppMode} reads it.
 */
export function getAdminPortalUrl(): string {
  const { protocol, host, origin } = window.location
  if (host.startsWith(APP_HOST_PREFIX)) {
    return `${protocol}//${ADMIN_HOST_PREFIX}${host.slice(APP_HOST_PREFIX.length)}`
  }
  return `${origin}/?mode=admin`
}

const AUTH_HOST_PREFIX = 'auth.'

/**
 * The Keycloak of the environment this page is served from (ADR-045), derived from the origin
 * for the reason {@link getAdminPortalUrl} is: `admin.staging.scrivio.nl` signs in at
 * `auth.staging.scrivio.nl`. `null` off a deployed hostname, where there is no Keycloak to derive.
 */
export function getKeycloakUrl(): string | null {
  const { protocol, host } = window.location
  const prefix = [APP_HOST_PREFIX, ADMIN_HOST_PREFIX].find((candidate) => host.startsWith(candidate))
  return prefix ? `${protocol}//${AUTH_HOST_PREFIX}${host.slice(prefix.length)}` : null
}

export const appMode = getAppMode()
export const isAdminPortal = appMode === 'admin'
export const isClientPortal = appMode === 'client'
export const isBehandelaarApp = appMode === 'behandelaar'
